import React from 'react'
import styled from 'styled-components'
import Header from '../common/header'
import Footer from '../common/footer'
import Spacer from '../lib/spacer'
import StoreLinks from '../common/store_links'
import characters from '../img/characters.webp'
import { InView } from 'react-intersection-observer'
import { LocalizedLink as Link, Trans, useTranslation } from '../lib/i18n'
import { FREE_STRETCH_COUNT, STRETCH_COUNT } from '../lib/product'
import { MONTHLY_PRICE, YEARLY_PRICE } from '../lib/facts'
import stretchesScreenshot from '../img/stretches-screenshot.webp'
import breakVideo from '../home/work_smarter/video/sample.mp4'
import dashboardScreenshot from '../img/dashboard-screenshot.webp'
import ComparisonTable, { SourceLink } from '../compare/table'
import { LAST_VERIFIED } from '../compare/competitors'
import { COMPARE_PAGES } from '../compare/pages'
import { DIFFERENCES, TABLE_APPS, TABLE_COLUMNS } from './content'
import { FaArrowRight } from 'react-icons/fa'

const COUNTS = {
    stretchCount: STRETCH_COUNT,
    freeStretchCount: FREE_STRETCH_COUNT,
    monthlyPrice: MONTHLY_PRICE,
    yearlyPrice: YEARLY_PRICE,
}

const PageWrapper = styled.div`
    background: #ffffff;
    color: #0f172a;
    overflow-x: hidden;
`

const Container = styled.div`
    max-width: 880px;
    margin: 0 auto;
    padding: 0 1.5rem;
`

const Hero = styled.section`
    background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
    border-bottom: 1px solid #e2e8f0;
    padding: 56px 0 52px;
    text-align: center;

    h1 {
        font-size: 2.85rem;
        font-weight: 800;
        color: #0f172a;
        letter-spacing: -1px;
        line-height: 1.2;
        max-width: 760px;
        margin: 0 auto 20px;
    }

    p.lead {
        color: #475569;
        font-size: 1.14rem;
        line-height: 1.72;
        max-width: 740px;
        margin: 0 auto;
    }

    p.sublead {
        color: #64748b;
        font-size: 1.05rem;
        line-height: 1.7;
        max-width: 720px;
        margin: 16px auto 0;
    }

    @media (max-width: 768px) {
        padding: 40px 0 44px;

        h1 {
            font-size: 2.15rem;
            letter-spacing: -0.6px;
        }

        p.lead {
            font-size: 1.05rem;
        }
    }
`

const CharactersImage = styled.img`
    display: block;
    width: 270px;
    max-width: 80%;
    height: auto;
    margin: 0 auto 28px;

    @media (max-width: 640px) {
        width: 210px;
        margin-bottom: 20px;
    }
`

const HeroActions = styled.div`
    display: flex;
    justify-content: center;
    margin: 32px 0 0;
`

const AudienceSection = styled.section`
    padding: 60px 0 52px;
    border-bottom: 1px solid #f1f5f9;

    h2 {
        font-size: 1.85rem;
        font-weight: 800;
        color: #0f172a;
        letter-spacing: -0.5px;
        line-height: 1.28;
        margin-bottom: 18px;
    }

    p {
        color: #475569;
        font-size: 1.05rem;
        line-height: 1.78;
        margin-bottom: 16px;
        max-width: 780px;

        &:last-child {
            margin-bottom: 0;
        }
    }

    @media (max-width: 768px) {
        padding: 44px 0 36px;

        h2 {
            font-size: 1.6rem;
        }
    }
`

const FeatureRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 48px;
    align-items: center;
    padding: 60px 0;
    border-bottom: 1px solid #f1f5f9;

    ${(props) =>
        props.$reverse &&
        `
        direction: rtl;
        > * {
            direction: ltr;
        }
    `}

    h2 {
        font-size: 1.85rem;
        font-weight: 800;
        color: #0f172a;
        letter-spacing: -0.5px;
        line-height: 1.28;
        margin-bottom: 16px;
    }

    p {
        color: #475569;
        font-size: 1.02rem;
        line-height: 1.75;
        margin-bottom: 16px;

        &:last-child {
            margin-bottom: 0;
        }
    }

    @media (max-width: 820px) {
        grid-template-columns: 1fr;
        gap: 32px;
        padding: 44px 0;
        direction: ltr;

        h2 {
            font-size: 1.6rem;
        }
    }
`

const VisualFrame = styled.div`
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 16px;
    padding: 12px;
    box-shadow: 0 4px 20px rgba(15, 23, 42, 0.04);

    img,
    video {
        display: block;
        width: 100%;
        border-radius: 10px;
        border: 1px solid #e2e8f0;
    }
`

const SplitGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
    padding: 50px 0;
    border-bottom: 1px solid #f1f5f9;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 20px;
        padding: 36px 0;
    }
`

const SplitCard = styled.div`
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 16px;
    padding: 32px 28px;

    h3 {
        font-size: 1.25rem;
        font-weight: 700;
        color: #0f172a;
        margin-bottom: 12px;
        letter-spacing: -0.3px;
        line-height: 1.35;
    }

    p {
        color: #475569;
        font-size: 0.98rem;
        line-height: 1.7;
        margin-bottom: 12px;

        &:last-child {
            margin-bottom: 0;
        }
    }
`

const PlanNote = styled.div`
    margin: 36px 0 54px;
    padding: 24px 28px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 14px;

    p {
        color: #334155;
        font-size: 1rem;
        line-height: 1.7;
        margin: 0;
    }

    a {
        color: #209fff;
        font-weight: 600;
        text-decoration: underline;

        &:hover {
            color: #1282d7;
        }
    }
`

const SectionHeader = styled.div`
    margin: 54px 0 28px;

    h2 {
        font-size: 1.85rem;
        font-weight: 800;
        color: #0f172a;
        letter-spacing: -0.5px;
        line-height: 1.3;
        margin-bottom: 12px;
    }

    p {
        color: #475569;
        font-size: 1.02rem;
        line-height: 1.7;
        margin: 0;
    }

    @media (max-width: 640px) {
        margin: 40px 0 20px;

        h2 {
            font-size: 1.55rem;
        }
    }
`

const CheckedDate = styled.p`
    color: #94a3b8;
    font-size: 0.85rem;
    line-height: 1.6;
    margin: 8px 0 28px;
`

const CompetitorGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 18px;
    margin-bottom: 28px;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`

const CompetitorCard = styled.div`
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 14px;
    padding: 22px 24px;

    h3 {
        font-size: 1.05rem;
        font-weight: 700;
        color: #0f172a;
        margin-bottom: 8px;
    }

    p {
        color: #475569;
        font-size: 0.94rem;
        line-height: 1.65;
        margin: 0;
    }
`

const ComparisonPillsWrap = styled.div`
    margin-top: 24px;
    padding: 24px 26px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 16px;

    h4 {
        font-size: 0.98rem;
        font-weight: 700;
        color: #0f172a;
        margin-bottom: 14px;
    }

    .pills {
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
    }
`

const CompareLinkPill = styled(Link)`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: #ffffff;
    border: 1px solid #cbd5e1;
    color: #334155;
    font-size: 0.88rem;
    font-weight: 600;
    padding: 7px 15px;
    border-radius: 9999px;
    text-decoration: none;
    transition: all 0.15s ease;

    svg {
        font-size: 0.72rem;
        color: #209fff;
        transition: transform 0.15s ease;
    }

    &:hover {
        border-color: #209fff;
        color: #209fff;

        svg {
            transform: translateX(2px);
        }
    }
`

const FactsList = styled.ul`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px 32px;
    margin: 20px 0 0;
    padding: 0;
    list-style: none;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 10px;
    }

    li {
        position: relative;
        padding-left: 22px;
        color: #475569;
        font-size: 0.96rem;
        line-height: 1.65;

        &::before {
            content: '•';
            position: absolute;
            left: 4px;
            top: -1px;
            color: #209fff;
            font-size: 1.2rem;
        }
    }
`

const FAQList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    margin-top: 20px;

    .faq-item {
        border-bottom: 1px solid #f1f5f9;
        padding-bottom: 20px;

        &:last-child {
            border-bottom: none;
            padding-bottom: 0;
        }

        h3 {
            font-size: 1.12rem;
            font-weight: 700;
            color: #0f172a;
            margin-bottom: 8px;
        }

        p {
            color: #475569;
            font-size: 0.98rem;
            line-height: 1.7;
            margin: 0;
        }
    }
`

const DownloadSection = styled.section`
    padding: 56px 0 72px;
    text-align: center;
    border-top: 1px solid #f1f5f9;
    margin-top: 50px;

    h2 {
        font-size: 2.1rem;
        font-weight: 800;
        color: #0f172a;
        letter-spacing: -0.5px;
        margin-bottom: 28px;
    }

    @media (max-width: 640px) {
        padding: 40px 0 60px;

        h2 {
            font-size: 1.6rem;
        }
    }
`

export default () => {
    const { t } = useTranslation(undefined, { keyPrefix: 'breakReminderApp' })
    const { t: tHero } = useTranslation(undefined, { keyPrefix: 'home.hero' })
    const list = (key) => t(key, { ...COUNTS, returnObjects: true })
    const tr = (key) => t(key, COUNTS)

    const sections = list('sections') || []
    const introParagraphs = list('intro') || []
    const whoIsItFor = t('whoIsItFor', { ...COUNTS, returnObjects: true })

    return (
        <PageWrapper>
            <Header />

            {/* Hero Section */}
            <Hero>
                <Container>
                    <CharactersImage
                        src={characters}
                        alt={tHero('charactersAlt')}
                    />
                    <h1>{t('heading')}</h1>
                    {introParagraphs.map((paragraph, index) => (
                        <p
                            key={paragraph}
                            className={index === 0 ? 'lead' : 'sublead'}
                        >
                            {paragraph}
                        </p>
                    ))}

                    <HeroActions>
                        <StoreLinks />
                    </HeroActions>
                </Container>
            </Hero>

            <Container>
                {/* Who is Keybreak for? */}
                {whoIsItFor?.heading && (
                    <AudienceSection>
                        <h2>{whoIsItFor.heading}</h2>
                        {whoIsItFor.body?.map((paragraph) => (
                            <p key={paragraph}>{paragraph}</p>
                        ))}
                    </AudienceSection>
                )}

                {/* Feature 1: Activity-Aware Timing */}
                {sections[0] && (
                    <FeatureRow>
                        <div>
                            <h2>{sections[0].heading}</h2>
                            {sections[0].body.map((paragraph) => (
                                <p key={paragraph}>{paragraph}</p>
                            ))}
                        </div>
                        <VisualFrame>
                            <img
                                src={dashboardScreenshot}
                                alt={t('screenshots.dashboard')}
                            />
                        </VisualFrame>
                    </FeatureRow>
                )}

                {/* Feature 2: Guided Desk Stretches */}
                {sections[1] && (
                    <FeatureRow $reverse>
                        <div>
                            <h2>{sections[1].heading}</h2>
                            {sections[1].body.map((paragraph) => (
                                <p key={paragraph}>{paragraph}</p>
                            ))}
                        </div>
                        <VisualFrame>
                            <InView
                                as="div"
                                triggerOnce
                                threshold={0.4}
                                onChange={(inView, entry) =>
                                    inView &&
                                    entry?.target
                                        ?.querySelector('video')
                                        ?.play()
                                        .catch(() => {})
                                }
                            >
                                <video
                                    poster={stretchesScreenshot}
                                    preload="metadata"
                                    muted
                                    loop
                                    playsInline
                                    controls
                                    aria-label={t('screenshots.stretch')}
                                >
                                    <source src={breakVideo} type="video/mp4" />
                                </video>
                            </InView>
                        </VisualFrame>
                    </FeatureRow>
                )}

                {/* Features 3 & 4: Personalisation & Privacy */}
                <SplitGrid>
                    {sections[2] && (
                        <SplitCard>
                            <h3>{sections[2].heading}</h3>
                            {sections[2].body.map((paragraph) => (
                                <p key={paragraph}>{paragraph}</p>
                            ))}
                        </SplitCard>
                    )}
                    {sections[3] && (
                        <SplitCard>
                            <h3>{sections[3].heading}</h3>
                            {sections[3].body.map((paragraph) => (
                                <p key={paragraph}>{paragraph}</p>
                            ))}
                        </SplitCard>
                    )}
                </SplitGrid>

                {/* Plan Note Callout */}
                <PlanNote>
                    <p>
                        <Trans
                            t={t}
                            i18nKey="planNote"
                            values={COUNTS}
                            components={{ 1: <Link to="/pricing" /> }}
                        />
                    </p>
                </PlanNote>

                {/* Comparison Section */}
                <SectionHeader>
                    <h2>{tr('differences.heading')}</h2>
                    <p>{tr('differences.intro')}</p>
                </SectionHeader>

                <ComparisonTable apps={TABLE_APPS} columns={TABLE_COLUMNS} t={t} />
                <CheckedDate>
                    {t('differences.checked', { date: LAST_VERIFIED })}
                </CheckedDate>

                <CompetitorGrid>
                    {DIFFERENCES.map(({ id, name, sources }) => (
                        <CompetitorCard key={id}>
                            <h3>{t('differences.comparedWith', { app: name })}</h3>
                            <p>
                                {tr(`differences.items.${id}`)}{' '}
                                {sources.map((href) => (
                                    <SourceLink key={href} href={href} />
                                ))}
                            </p>
                        </CompetitorCard>
                    ))}
                </CompetitorGrid>

                <p style={{ color: '#475569', lineHeight: 1.75, marginBottom: 20 }}>
                    {tr('differences.outro')}
                </p>

                <ComparisonPillsWrap>
                    <h4>{tr('differences.moreComparisons')}</h4>
                    <div className="pills">
                        {COMPARE_PAGES.map(({ slug, h1 }) => (
                            <CompareLinkPill key={slug} to={`/blog/${slug}`}>
                                {h1} <FaArrowRight />
                            </CompareLinkPill>
                        ))}
                    </div>
                </ComparisonPillsWrap>

                {/* Quick Facts / Specifications */}
                <SectionHeader id="facts">
                    <h2>{tr('facts.heading')}</h2>
                </SectionHeader>
                <FactsList>
                    {list('facts.items').map((fact) => (
                        <li key={fact}>{fact}</li>
                    ))}
                </FactsList>

                {/* Frequently Asked Questions */}
                <SectionHeader>
                    <h2>{t('faqHeading')}</h2>
                </SectionHeader>
                <FAQList>
                    {list('faq').map(({ q, a }) => (
                        <div key={q} className="faq-item">
                            <h3>{q}</h3>
                            <p>{a}</p>
                        </div>
                    ))}
                </FAQList>

                {/* Download Section */}
                <DownloadSection>
                    <h2>
                        {tHero('titleLine1')} {tHero('titleLine2')}
                    </h2>
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <StoreLinks />
                    </div>
                </DownloadSection>
            </Container>

            <Footer />
        </PageWrapper>
    )
}
